import { Button } from "@/components/ui/button";
import { useSignIn } from "@reboot-dev/reboot-react";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthLayout } from ".";
import { Login } from "./Login";
import { Signup } from "./Signup";

// When this browser tab last handed sign-in to the backend's OAuth
// flow, as epoch milliseconds. Lives in `sessionStorage` so it
// survives the round-trip out to the backend and Ory and back.
const SIGN_IN_STARTED_KEY = "reboot.signIn.startedAt";

// How recently we must have handed off for a return here with no
// login challenge to mean the flow bounced straight back, rather
// than someone arriving at the sign-in page of their own accord.
const SIGN_IN_BOUNCE_MILLISECONDS = 30_000;

/**
 * A sign-in page that is both the entry point into the backend's
 * OAuth flow and the login UI that flow comes back to, told apart by
 * whether Ory handed back a flow to render.
 *
 * With nothing, `useSignIn()` sends the browser to the backend's
 * `/__/oauth/start`; the backend redirects to Ory, and Ory sends the
 * browser back here carrying the login — either a `login_challenge`
 * param, or a Kratos `flow` that holds the challenge in
 * `oauth2_login_challenge` (how Ory routes it without a custom domain,
 * as in local dev). That hand-off needs no click, but a browser that
 * comes straight back with neither gets a button, so a flow that
 * cannot make progress stops instead of bouncing forever.
 *
 * With one, `form` renders: the Kratos flow that answers it.
 */
const SignInFlow = ({
  callToAction,
  form,
}: {
  callToAction: string;
  form: React.ReactNode;
}) => {
  const [searchParams] = useSearchParams();
  const signIn = useSignIn();
  const [bounced, setBounced] = useState(false);

  // React's `StrictMode` mounts every component twice in
  // development; without this the second mount would read back the
  // timestamp the first one just wrote and report a bounce.
  const startedRef = useRef(false);

  const loginChallenge = searchParams.get("login_challenge");
  // Ory hands the login back either as a raw `login_challenge` (a
  // custom domain resolves this console as the OAuth login UI) or as a
  // Kratos `flow` carrying the challenge in `oauth2_login_challenge`
  // (routed through the Account Experience first, as local dev is,
  // having no custom domain). Either means Ory sent a flow to render.
  const flowId = searchParams.get("flow");
  const returnedFromOry = loginChallenge !== null || flowId !== null;

  const start = () => {
    window.sessionStorage.setItem(SIGN_IN_STARTED_KEY, String(Date.now()));
    signIn(searchParams.get("return_to") ?? window.location.origin + "/");
  };

  useEffect(() => {
    if (returnedFromOry) {
      // The flow reached Ory and came back with a challenge (or a
      // Kratos flow that holds one), so it is making progress and the
      // hand-off we recorded has served out its purpose.
      window.sessionStorage.removeItem(SIGN_IN_STARTED_KEY);
      return;
    }

    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    const startedAt = Number(
      window.sessionStorage.getItem(SIGN_IN_STARTED_KEY) ?? ""
    );
    if (Date.now() - startedAt < SIGN_IN_BOUNCE_MILLISECONDS) {
      window.sessionStorage.removeItem(SIGN_IN_STARTED_KEY);
      setBounced(true);
      return;
    }

    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnedFromOry]);

  const body = () => {
    if (returnedFromOry) {
      return form;
    }
    if (bounced) {
      return (
        <div className="flex flex-col gap-4 px-12 pt-12">
          <div>
            We couldn't complete your sign-in. Please try again, or contact
            support@reboot.dev if the problem persists.
          </div>
          <Button className="w-full" onClick={start}>
            {callToAction}
          </Button>
        </div>
      );
    }
    return <div className="h-[576px] px-12 pt-12">Signing you in...</div>;
  };

  return <AuthLayout callToAction={callToAction}>{body()}</AuthLayout>;
};

export const SignInPage = () => (
  <SignInFlow callToAction="Sign in" form={<Login />} />
);

export const SignUpPage = () => (
  <SignInFlow callToAction="Sign up" form={<Signup />} />
);
